import { cva, cx } from 'class-variance-authority';
import { useFormDisabled } from 'component/form/form-disabled';
import { Button, type ButtonProps, composeRenderProps } from 'react-aria-components';
import { makeTestID } from 'util/stdtypes';

import styles from './text-button.module.css';

const button = cva(styles.button, {
    variants: {
        variant: {
            secondary: null,
            primary: styles.primary,
        },
    },
    defaultVariants: { variant: 'secondary' },
});

/**
 * Props for {@link TextButton}.
 *
 * @public */
export type TextButtonProps = Omit<ButtonProps, 'children'> & {
    text: string;
    variant?: 'primary' | 'secondary';
    colorOverride?: string;
    testID?: string;
    ref?: React.Ref<HTMLButtonElement>;
};

/** @public */
export function TextButton({
    text,
    variant,
    colorOverride,
    testID,
    className,
    style,
    isDisabled: ownIsDisabled,
    ref,
    ...rest
}: TextButtonProps) {
    const formIsDisabled = useFormDisabled();
    const isDisabled = formIsDisabled || ownIsDisabled;

    // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
    const composedClassName = composeRenderProps(className, (callerClass) => cx(button({ variant }), 'extendedClickArea', callerClass));
    const composedStyle = colorOverride ? composeRenderProps(style, (callerStyle) => ({ ...callerStyle, color: colorOverride })) : style;

    return (
        <Button
            data-testid={makeTestID({ testID, text })}
            isDisabled={isDisabled}
            ref={ref}
            {...rest}
            className={composedClassName}
            style={composedStyle}
        >
            {text}
        </Button>
    );
}
